import { useState, useCallback, useRef, useEffect } from 'react';
import type { Language } from '../i18n';
import type { ChatSuggestion } from '../components/ChatSuggestions';

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  suggestions?: ChatSuggestion[];
}

export interface FormData {
  companySize: string;
  revenueRange: string;
  industry: string;
  geography: string[];
  softwareTools: string[];
  role: string;
  // Sales intelligence fields
  hasSoftwarePartner?: 'yes' | 'no' | 'looking';
  itStructure?: 'internal' | 'outsourced' | 'mixed' | 'none';
  lookingForPartner?: 'yes' | 'no' | 'better';
  // Context fields for better conversation
  primaryChallenge?: 'data-entry' | 'reporting' | 'approvals' | 'communication' | 'inventory' | 'other';
  decisionTimeline?: 'exploring' | 'this-year' | 'urgent';
  automationExperience?: 'none' | 'some' | 'advanced';
}

// Token usage tracking
export interface TokenUsage {
  promptTokens: number;
  completionTokens: number;
  totalTokens: number;
  model: string;
}

// Assessment scores from tool call
export interface AssessmentScores {
  data_entry: number;
  sales_marketing: number;
  accounting: number;
  project_mgmt?: number;
  supply_chain?: number;
  estimated_hours_wasted_weekly: number;
  estimated_annual_cost?: number;
  current_software_spend?: number;
  inefficiency_cause?: 'none' | 'supplier' | 'adoption' | 'technology';
  top_pain_points?: string[];
  quick_wins?: string[];
  risk_factors?: string[];
  sales_intelligence?: {
    opportunity_score?: number;
    software_situation?: object;
    intent_signals?: object;
    ai_status?: object;
    budget_signals?: object;
    recommended_approach?: string;
    key_talking_points?: string[];
  };
}

// Violation tracking
export type ViolationType = 'jailbreak' | 'off_topic' | 'harassment' | 'spam';

export interface ViolationSummary {
  totalCount: number;
  byType: Record<ViolationType, number>;
  riskScore: number;
  flagged: boolean;
  violations: Array<{
    type: ViolationType;
    message: string;
    timestamp: string;
    severity: 1 | 2 | 3;
  }>;
}

// Startup form data type
export interface StartupFormData {
  ideaDescription: string;
  projectStage: 'idea' | 'concept' | 'prototype' | 'mvp' | 'revenue';
  targetSector: string;
  founderBackground: 'technical' | 'business' | 'domain' | 'mixed';
  teamSize: 'solo' | '2-3' | '4-10' | '10+';
  investmentReadiness: 'bootstrapping' | 'seeking-seed' | 'have-funding' | 'self-funded';
  geographicFocus: string[];
  timeline: 'exploring' | '3-6-months' | 'actively-building';
  hasExistingProduct: boolean;
}

// Project form data type
export interface ProjectFormData {
  projectDescription: string;
  projectType: 'web-app' | 'mobile-app' | 'internal-tool' | 'api-integration' | 'e-commerce' | 'ai-ml' | 'other';
  targetUsers: 'internal' | 'external' | 'both' | 'b2b';
  industry: string;
  companySize: 'solo' | '2-10' | '11-50' | '51-200' | '200+';
  budget: string;
  timeline: string;
  technicalTeam: 'none' | 'some' | 'full' | 'looking';
  existingSystems: string;
  hasDesign: boolean;
  geographicFocus: string[];
}

interface UseChatOptions {
  formData: FormData | StartupFormData | ProjectFormData | Record<string, unknown>;
  language: Language;
  sessionId?: string;
  chatEndpoint?: string; // default: '/api/chat'
  onComplete?: (scores?: AssessmentScores) => void;
}

interface UseChatReturn {
  messages: ChatMessage[];
  isLoading: boolean;
  isThinking: boolean;
  isComplete: boolean;
  error: string | null;
  scores: AssessmentScores | null;
  tokenUsage: TokenUsage | null;
  violations: ViolationSummary | null;
  currentSuggestions: ChatSuggestion[];
  userMessageCount: number;
  canForceComplete: boolean;
  pendingAutoComplete: boolean;
  sendMessage: (content: string) => Promise<void>;
  initChat: () => Promise<void>;
  forceComplete: () => void;
  reset: () => void;
}

const API_BASE = '/api';

export function useChat({ formData, language, sessionId, chatEndpoint = '/api/chat', onComplete }: UseChatOptions): UseChatReturn {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isThinking, setIsThinking] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [scores, setScores] = useState<AssessmentScores | null>(null);
  const [tokenUsage, setTokenUsage] = useState<TokenUsage | null>(null);
  const [violations, setViolations] = useState<ViolationSummary | null>(null);
  const [currentSuggestions, setCurrentSuggestions] = useState<ChatSuggestion[]>([]);
  const abortControllerRef = useRef<AbortController | null>(null);

  const initChat = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE}${chatEndpoint.replace('/api', '')}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          messages: [],
          formData,
          language,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error: ${response.status}`);
      }

      const data = await response.json();

      // Parse welcome message — startup/project return JSON strings with {text, suggestions}
      let welcomeText = data.message;
      let welcomeSuggestions: ChatSuggestion[] = [];
      if (typeof data.message === 'string' && data.message.trim().startsWith('{')) {
        try {
          const parsed = JSON.parse(data.message);
          if (parsed.text && typeof parsed.text === 'string') {
            welcomeText = parsed.text;
            if (Array.isArray(parsed.suggestions)) {
              welcomeSuggestions = parsed.suggestions
                .filter((s: unknown): s is string => typeof s === 'string')
                .slice(0, 4)
                .map((text: string, i: number) => ({ id: String(i), text }));
            }
          }
        } catch {
          // Not valid JSON, use as-is
        }
      }

      setMessages([
        {
          role: 'assistant',
          content: welcomeText,
          timestamp: new Date(),
        },
      ]);

      if (welcomeSuggestions.length > 0) {
        setCurrentSuggestions(welcomeSuggestions);
      }

      if (data.isComplete) {
        setIsComplete(true);
        onComplete?.();
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to initialize chat';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, [formData, language, chatEndpoint, onComplete]);

  const sendMessage = useCallback(
    async (content: string) => {
      if (!content.trim() || isLoading) return;

      // Cancel any pending request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();

      // Add user message
      const userMessage: ChatMessage = {
        role: 'user',
        content: content.trim(),
        timestamp: new Date(),
      };

      // If we've already passed the completion threshold and the assistant's
      // last turn was a question, this user reply is the closing turn —
      // skip the backend call and mark the assessment complete.
      const userMessagesSoFar = messages.filter(m => m.role === 'user').length;
      const lastAssistant = [...messages].reverse().find(m => m.role === 'assistant')?.content?.trim() ?? '';
      const shouldAutoComplete =
        userMessagesSoFar >= 10 && !isComplete && !scores && lastAssistant.endsWith('?');

      if (shouldAutoComplete) {
        setMessages((prev) => [...prev, userMessage]);
        setCurrentSuggestions([]);
        setIsComplete(true);
        onComplete?.();
        return;
      }

      setMessages((prev) => [...prev, userMessage]);
      setIsLoading(true);
      setError(null);
      setCurrentSuggestions([]); // Clear suggestions when user sends a message

      // Add placeholder assistant message for streaming
      const assistantTimestamp = new Date();
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: '',
          timestamp: assistantTimestamp,
        },
      ]);

      try {
        // Prepare messages for API (exclude timestamps, and the placeholder)
        const apiMessages = [...messages, userMessage].map((m) => ({
          role: m.role,
          content: m.content,
        }));

        const response = await fetch(`${API_BASE}${chatEndpoint.replace('/api', '')}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'text/event-stream',
            ...(sessionId ? { 'X-Session-ID': sessionId } : {}),
          },
          body: JSON.stringify({
            messages: apiMessages,
            formData,
            language,
            assessmentId: sessionId,
          }),
          signal: abortControllerRef.current.signal,
        });

        // Handle throttling (429)
        if (response.status === 429) {
          const errorData = await response.json();
          setMessages((prev) => prev.filter((m) => m.content.length > 0));
          if (errorData.violations) {
            setViolations(errorData.violations);
          }
          throw new Error(errorData.error || 'Session throttled');
        }

        if (!response.ok) {
          // Remove empty assistant placeholder
          setMessages((prev) => prev.filter((m) => m.content.length > 0));
          throw new Error(`HTTP error: ${response.status}`);
        }

        const reader = response.body?.getReader();
        if (!reader) {
          throw new Error('No response body');
        }

        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const data = JSON.parse(line.slice(6));

                // Ignore keep-alive pings and connection events
                if (data.ping || data.connected) {
                  continue;
                }

                if (data.error) {
                  // Remove empty assistant placeholder and throw
                  setMessages((prev) => prev.filter((m) => m.content.length > 0));
                  throw new Error(data.error);
                }

                // Handle reasoning event - model is thinking
                if (data.reasoning) {
                  setIsThinking(true);
                }

                if (data.chunk) {
                  // Content arrived, no longer just thinking
                  setIsThinking(false);
                  // Update last assistant message with new chunk
                  setMessages((prev) => {
                    const updated = [...prev];
                    const last = updated[updated.length - 1];
                    if (last && last.role === 'assistant') {
                      updated[updated.length - 1] = {
                        ...last,
                        content: last.content + data.chunk,
                      };
                    }
                    return updated;
                  });
                }

                // Handle scores from tool call
                if (data.scores) {
                  console.log('[useChat] Received assessment scores:', data.scores);
                  setScores(data.scores as AssessmentScores);
                }

                // Handle suggestions event (sent separately from text)
                if (data.suggestions && Array.isArray(data.suggestions)) {
                  const suggestions: ChatSuggestion[] = data.suggestions
                    .slice(0, 4)
                    .map((text: string, i: number) => ({
                      id: String(i),
                      text: String(text),
                    }));
                  setCurrentSuggestions(suggestions);
                }

                // Handle usage data
                if (data.usage) {
                  console.log('[useChat] Received token usage:', data.usage);
                  // Accumulate token usage across messages
                  setTokenUsage((prev) => {
                    if (!prev) {
                      return data.usage as TokenUsage;
                    }
                    return {
                      promptTokens: prev.promptTokens + (data.usage as TokenUsage).promptTokens,
                      completionTokens: prev.completionTokens + (data.usage as TokenUsage).completionTokens,
                      totalTokens: prev.totalTokens + (data.usage as TokenUsage).totalTokens,
                      model: (data.usage as TokenUsage).model,
                    };
                  });
                }

                if (data.done) {
                  // Use fullMessage if provided for clean content (already parsed by backend)
                  if (data.fullMessage) {
                    setMessages((prev) => {
                      const updated = [...prev];
                      const last = updated[updated.length - 1];
                      if (last && last.role === 'assistant') {
                        updated[updated.length - 1] = {
                          ...last,
                          content: data.fullMessage,
                        };
                      }
                      return updated;
                    });
                  }

                  // Update violations if present
                  if (data.violations) {
                    setViolations(data.violations as ViolationSummary);
                  }

                  if (data.isComplete) {
                    setIsComplete(true);
                    // Pass scores to onComplete if available
                    onComplete?.(data.scores || undefined);
                  }
                }
              } catch (parseErr) {
                // Skip invalid JSON lines (but re-throw actual errors)
                if (parseErr instanceof Error && parseErr.message !== 'Unexpected end of JSON input') {
                  throw parseErr;
                }
              }
            }
          }
        }
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') {
          return;
        }
        const message = err instanceof Error ? err.message : 'Failed to send message';
        setError(message);
        // Remove empty assistant message on error
        setMessages((prev) => prev.filter((m) => m.content.length > 0));
      } finally {
        setIsLoading(false);
      }
    },
    [messages, formData, language, chatEndpoint, isLoading, isComplete, scores, sessionId, onComplete]
  );

  const reset = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    setMessages([]);
    setIsLoading(false);
    setIsThinking(false);
    setIsComplete(false);
    setError(null);
    setScores(null);
    setTokenUsage(null);
    setViolations(null);
    setCurrentSuggestions([]);
  }, []);

  // Calculate user message count for completion tracking
  const userMessageCount = messages.filter(m => m.role === 'user').length;

  // Detect if the assistant's last message is an unanswered question.
  // In that case we must not show a "complete" CTA next to a live question —
  // instead we let the user answer once more, then auto-complete on their reply.
  const lastAssistantContent = [...messages].reverse().find(m => m.role === 'assistant')?.content?.trim() ?? '';
  const lastAssistantIsQuestion = lastAssistantContent.endsWith('?');

  const reachedCompletionThreshold = userMessageCount >= 10 && !isComplete && !scores && !isLoading;
  const pendingAutoComplete = reachedCompletionThreshold && lastAssistantIsQuestion;
  const canForceComplete = reachedCompletionThreshold && !lastAssistantIsQuestion;

  // Force complete the assessment without scores (fallback when LLM doesn't call tool)
  const forceComplete = useCallback(() => {
    if (canForceComplete) {
      console.log('[useChat] Force completing assessment after', userMessageCount, 'user messages');
      setIsComplete(true);
      onComplete?.();
    }
  }, [canForceComplete, userMessageCount, onComplete]);

  // Cleanup on unmount - abort any pending requests
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return {
    messages,
    isLoading,
    isThinking,
    isComplete,
    error,
    scores,
    tokenUsage,
    violations,
    currentSuggestions,
    userMessageCount,
    canForceComplete,
    pendingAutoComplete,
    sendMessage,
    initChat,
    forceComplete,
    reset,
  };
}

export async function generateReport(
  messages: ChatMessage[],
  formData: FormData,
  language: Language,
  assessmentScores?: AssessmentScores | null
): Promise<unknown> {
  const transcript = messages.map((m) => ({
    role: m.role,
    content: m.content,
  }));

  const requestBody = {
    transcript,
    formData,
    language,
    assessmentScores: assessmentScores ?? undefined,
  };

  console.log('[generateReport] Sending request:', JSON.stringify(requestBody, null, 2));

  const response = await fetch(`${API_BASE}/generate-report`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestBody),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    console.error('[generateReport] Error response:', errorData);
    throw new Error(`HTTP error: ${response.status} - ${JSON.stringify(errorData)}`);
  }

  return response.json();
}

export async function generateStartupReport(
  messages: ChatMessage[],
  formData: StartupFormData,
  language: Language,
  startupScores?: Record<string, unknown> | null
): Promise<unknown> {
  const transcript = messages.map((m) => ({
    role: m.role,
    content: m.content,
  }));

  const requestBody = {
    transcript,
    formData,
    language,
    startupScores: startupScores ?? undefined,
  };

  console.log('[generateStartupReport] Sending request');

  const response = await fetch(`${API_BASE}/generate-startup-report`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestBody),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    console.error('[generateStartupReport] Error response:', errorData);
    throw new Error(`HTTP error: ${response.status} - ${JSON.stringify(errorData)}`);
  }

  return response.json();
}

export async function generateProjectReport(
  messages: ChatMessage[],
  formData: ProjectFormData,
  language: Language,
  projectScores?: Record<string, unknown> | null
): Promise<unknown> {
  const transcript = messages.map((m) => ({
    role: m.role,
    content: m.content,
  }));

  const requestBody = {
    transcript,
    formData,
    language,
    projectScores: projectScores ?? undefined,
  };

  console.log('[generateProjectReport] Sending request');

  const response = await fetch(`${API_BASE}/generate-project-report`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestBody),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    console.error('[generateProjectReport] Error response:', errorData);
    throw new Error(`HTTP error: ${response.status} - ${JSON.stringify(errorData)}`);
  }

  return response.json();
}
